import { collection, getDocs, query, where } from 'firebase/firestore';
import { useCallback, useEffect, useMemo } from 'react';
import { ArchivedOrder, ArchivedOrderGroup, Order } from '../../apiTypes';
import { useAdmin } from '../../contexts/admin.context';
import { db } from '../../firebase';
import { setDoc, useCollection, useCollectionOnce } from '../../utils/api';
import { getDayTimeRange } from '../../utils/dates';
import getDateKey from '../../utils/dates/getDateKey';
import getDateKeys from '../../utils/dates/getDateKeys';
import getTimeRangeFromDateKey from '../../utils/dates/getTimeRangeFromDateKey';

export const useGetArchivedOrders = (timeRange: [number, number]) => {
  const { cafeSlug } = useAdmin();

  const tadayTimeRange = useMemo(() => getDayTimeRange(Date.now()), []);

  const { data: todayOrders } = useCollectionOnce<Order>(`cafes/${cafeSlug}/orders`, {
    where: ['paidTime', '>', tadayTimeRange[0]],
    where2: ['paidTime', '<', tadayTimeRange[1]],
  });

  /** @todo remove */
  const startDate = cafeSlug === 'lm64cv5j' && timeRange[0] < 1711494000000 ? 1711494000000 : timeRange[0];

  const { data, isValidating } = useCollection<ArchivedOrderGroup>(`cafes/${cafeSlug}/archivedOrders`, {
    where: ['dateKey', '>=', getDateKey(startDate)],
    where2: ['dateKey', '<=', getDateKey(timeRange[1])],
  });

  const todayDayKey = useMemo(() => getDateKey(new Date()), []);

  const dateKeys = useMemo(() => getDateKeys(timeRange), [timeRange]);

  const hasToday = useMemo(() => dateKeys.includes(todayDayKey), [dateKeys, todayDayKey]);

  const missedDateKeys = useMemo(
    () =>
      isValidating || !data ? [] : dateKeys.filter((dk) => !data.some((doc) => doc.dateKey === dk) && dk < todayDayKey),
    [data, dateKeys, isValidating, todayDayKey]
  );

  const firstMissedKey = cafeSlug === 'lm64cv5j' && timeRange[0] < 1711494000000 ? undefined : missedDateKeys[0];

  const generateOrderGroups = useCallback(
    async (dateKey: number) => {
      const timeRange = getTimeRangeFromDateKey(dateKey);

      const documentSnapshots = await getDocs(
        query(
          collection(db, 'cafes', cafeSlug, 'orders'),
          where('paidTime', '>', timeRange[0]),
          where('paidTime', '<', timeRange[1])
        )
      );

      const orders: Order[] = [];
      documentSnapshots.forEach((doc) => {
        const data = doc.data() as Order;
        orders.push(data);
      });

      const archivedOrders: ArchivedOrder[] = orders.map(({ logs, items, ...order }) => ({
        ...order,
        items: Object.fromEntries(
          Object.entries(items).map(
            ([
              key,
              { status, queue, variation, comment, addTime, startProcessingTime, modifications, printed, ...item },
            ]) => [
              key,
              {
                ...item,
                modifications: modifications
                  ? modifications.map(({ name, price }) => ({ name, price: price || null, components: [] }))
                  : null,
              },
            ]
          )
        ),
      }));

      await setDoc<ArchivedOrderGroup>(`cafes/${cafeSlug}/archivedOrders/${dateKey}`, {
        dateKey,
        orders: archivedOrders,
      });
    },
    [cafeSlug]
  );

  const archivedOrders = useMemo(() => {
    if (firstMissedKey) {
      return [];
    }
    const archivedData = data?.map((i) => i.orders).flat() || [];
    if (hasToday) {
      return [...archivedData, ...(todayOrders || [])];
    }
    return archivedData;
  }, [data, firstMissedKey, hasToday, todayOrders]);

  useEffect(() => {
    if (firstMissedKey) {
      generateOrderGroups(firstMissedKey);
    }
  }, [firstMissedKey, generateOrderGroups]);

  return {
    archivedOrders,
    isLoading: isValidating || !!firstMissedKey,
  };
};
